<template>
    <div ref="dropdownParent"
         class="dropdown">
        <div v-show="!isDropdownOpen"
             class="dropdown-placeholder"
             @click="openDropdown">
            <div class="d-flex align-items-center">
                <svg class="search-icon"
                     xmlns="http://www.w3.org/2000/svg"
                     viewBox="0 0 512 512">
                    <path :d="`M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7
                     40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z`" />
                </svg>
                <div class="placeholder ml-1">
                    {{ placeholder }}
                </div>
            </div>
            <i class="fa-solid fa-chevron-down down-arrow" />
        </div>
        <div v-if="isDropdownOpen"
             class="dropdown-search">
            <input ref="searchInput"
                   v-model="search"
                   type="text"
                   :placeholder="getTrans('buy-pages.typeSelectCrypto')">
            <div class="close-icon"
                 @click="closeDropdown">
                <svg xmlns="http://www.w3.org/2000/svg"
                     fill="currentColor"
                     height="12"
                     width="12"
                     viewBox="0 0 24 24"
                     class="sc-1pyr0bh-0 KQOVh">
                    <path d="M18 6L6 18M18 18L6 6"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-miterlimit="10"
                          stroke-linecap="round"
                          stroke-linejoin="round" />
                    <svg />
                </svg>
            </div>
            <div class="dropdown-picker">
                <div v-if="filteredProviderOptions.length > 0">
                    <div v-for="(item, index) in filteredProviderOptions"
                         :key="index"
                         class="d-flex align-items-center justify-content-between provider-option"
                         @click="setValue($event)">
                        <div class="provider-name mr-2">
                            {{ item.name }}
                        </div>
                    </div>
                </div>
                <div v-else
                     class="no-results">
                    <div>{{ getTrans('header.searchNoResults') }}</div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { getTrans } from '@/js/helpers/getTrans';

    export default {
        name: 'ProviderDropdown',
        props: {
            options: {
                required: true,
                type: Array,
            },
            placeholder: {
                type: String,
                required: true,
            },
        },
        data() {
            return {
                isDropdownOpen: false,
                search: '',
            };
        },
        computed: {
            filteredProviderOptions() {
                return this.options.filter((item) => item.name.toLowerCase().includes(this.search.toLowerCase()));
            },
        },
        mounted() {
            document.addEventListener('click', this.handleClickOutside);
        },
        beforeUnmount() {
            document.removeEventListener('click', this.handleClickOutside);
        },
        methods: {
            getTrans,
            openDropdown() {
                this.isDropdownOpen = true;
                this.$nextTick(() => {
                    this.$refs.searchInput.focus();
                });
            },
            closeDropdown() {
                this.isDropdownOpen = false;
            },
            setValue(event) {
                this.isDropdownOpen = false;
                const placeholder = this.$el.querySelector('.placeholder');
                const selectedValue = this.options.find((option) => option.name === event.target.innerText);
                placeholder.innerText = selectedValue.name;
                this.$emit('selectProvider', selectedValue);
            },
            handleClickOutside(event) {
                if (this.isDropdownOpen) {
                    const search = this.$refs.dropdownParent;
                    const isClickOutsideSearch = search !== event.target && !search.contains(event.target);
                    if (isClickOutsideSearch) {
                        this.closeDropdown();
                    }
                }
            },
        },
    };
</script>

<style scoped lang="scss">
@use "@/sass/partials/z-index" as ZIndex;
@use "@/sass/partials/mixins" as mixins;
@use "@/sass/design-system/variables" as variables;
@use "@/sass/partials/variables" as legacyVariables;

.dropdown {
    position: relative;
    height: 40px;

    input {
        width: 100%;
        border: none;
        background-color: variables.$baseWhite;
        font-size: 13px;

        &:focus-visible {
            outline: none;
        }

        &::placeholder {
            font-weight: 500;
        }
    }

    .placeholder {
        color: #6f48c5;
        font-size: 13px;
        font-weight: 500;
    }

    .down-arrow {
        color: #6f48c5;
        font-size: 14px;
    }
}

.dropdown-placeholder {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px;
    border: 1px solid #6f48c5;
    border-radius: 8px;
    cursor: pointer;

    &::after {
        display: none;
    }

    svg {
        width: 14px;
        height: 14px;
        margin: 0;
        stroke: #6f48c5;
    }
}

.dropdown-search {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 7px 8px;
    border: 1px solid #ced4da;
    background-color: variables.$baseWhite;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;

    .close-icon {
        display: flex;
        width: 16px;
        height: 16px;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        background-color: rgb(207, 214, 228);
        -webkit-box-align: center;
        -webkit-box-pack: center;
        color: variables.$baseWhite;
        cursor: pointer;

        svg {
            width: 12px;
            height: 12px;
        }
    }
}

.dropdown-picker {
    position: absolute;
    z-index: ZIndex.$dropdown-picker-z-index;
    top: 37px;
    right: 0;
    left: 0;
    display: flex;
    max-height: 323px;
    flex-direction: column;
    padding: 8px 10px 10px 10px;
    border: 1px solid #ced4da;
    background-color: variables.$baseWhite;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    overflow-y: auto;

    .provider-option {
        padding: 8px;
        border-radius: 8px;
        color: variables.$baseBlack;
        cursor: pointer;
        font-size: 14px;
        font-weight: 400;

        .provider-name {
            @include mixins.one-line-clamp;
            color: variables.$baseBlack;
            font-size: 14px;
            font-weight: 600;
        }

        &:hover {
            background-color: #f0f0f0;
        }
    }

    .no-results {
        padding: 10px;
        font-size: 15px;
        font-weight: 400;
    }
}
</style>
